import React, { useState } from "react";
import { Contract } from "ethers";
import { Panel } from "./Panel";
import { readContract, getFee, estimateContract } from "../../lib/api";
import { useWallet } from "../../contexts/WalletContext";

function parseArg(raw, type) {
  const v = (raw ?? "").trim();
  if (type && type.endsWith("]")) { try { return JSON.parse(v); } catch { return v.split(",").map((s) => s.trim()); } }
  if (type === "bool") return v === "true" || v === "1";
  return v;
}

function FuncForm({ address, abi, fn, mode }) {
  const { account, onBlokista, getSigner, connect, refreshBalance } = useWallet();
  const [args, setArgs] = useState(fn.inputs.map(() => ""));
  const [busy, setBusy] = useState(false);
  const [out, setOut] = useState(null);
  const [err, setErr] = useState("");
  const [est, setEst] = useState(null);
  const [estBusy, setEstBusy] = useState(false);

  const estimate = async () => {
    setEstBusy(true); setEst(null);
    try {
      const parsed = fn.inputs.map((inp, i) => parseArg(args[i], inp.type));
      const r = await estimateContract({ address, abi, method: fn.name, args: parsed });
      setEst(r);
    } catch (e) {
      setEst({ error: e?.response?.data?.detail || "Cannot estimate" });
    } finally { setEstBusy(false); }
  };

  const runRead = async () => {
    setBusy(true); setErr(""); setOut(null);
    try {
      const parsed = fn.inputs.map((inp, i) => parseArg(args[i], inp.type));
      const r = await readContract({ address, abi, method: fn.name, args: parsed });
      setOut(JSON.stringify(r.result));
    } catch (e) {
      setErr(e?.response?.data?.detail || "Query failed");
    } finally { setBusy(false); }
  };

  const runWrite = async () => {
    setBusy(true); setErr(""); setOut(null);
    try {
      const signer = await getSigner();
      const c = new Contract(address, abi, signer);
      const parsed = fn.inputs.map((inp, i) => parseArg(args[i], inp.type));
      let overrides = {};
      try { const fee = await getFee(); overrides = { gasPrice: BigInt(fee.gasPriceWei) }; } catch { /* wallet estimates */ }
      const tx = await c[fn.name](...parsed, overrides);
      setOut("Broadcasting… " + tx.hash);
      const receipt = await tx.wait();
      setOut("Tx " + tx.hash + (receipt.status === 1 ? " ✓ confirmed" : " ✗ reverted"));
      refreshBalance(account);
    } catch (e) {
      setErr(e?.shortMessage || e?.info?.error?.message || e?.message || "Transaction failed");
    } finally { setBusy(false); }
  };

  const canWrite = mode === "write" && account && onBlokista;

  return (
    <div className="p-3 rounded-lg" style={{ background: "var(--bg-2)", border: "1px solid var(--border)" }} data-testid={`fn-${fn.name}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="font-mono text-[13px]" style={{ color: mode === "read" ? "var(--blue)" : "var(--amber)" }}>{fn.name}</span>
        <span className="pill pill-muted">{fn.stateMutability}</span>
      </div>
      {fn.inputs.map((inp, i) => (
        <input key={i} className="input-dark mb-2 text-[12px]" placeholder={`${inp.name || "arg" + i} (${inp.type})`}
          value={args[i]} onChange={(e) => { const a = [...args]; a[i] = e.target.value; setArgs(a); }} data-testid={`fn-${fn.name}-arg-${i}`} />
      ))}
      {mode === "read" ? (
        <button className="btn-amber w-full py-2 text-[12px]" onClick={runRead} disabled={busy} data-testid={`fn-${fn.name}-run`}>
          {busy ? "…" : "Query"}
        </button>
      ) : (
        <div className="flex gap-2">
          <button className="nav-link flex-1" style={{ padding: "8px" }} onClick={estimate} disabled={estBusy} data-testid={`fn-${fn.name}-estimate`}>
            {estBusy ? "…" : <><i className="fa-solid fa-gauge-high mr-1.5" />Estimate</>}
          </button>
          {!account ? (
            <button className="btn-amber flex-1 py-2 text-[12px]" onClick={connect} data-testid={`fn-${fn.name}-connect`}>
              <i className="fa-solid fa-wallet mr-1.5" /> Connect to Write
            </button>
          ) : !onBlokista ? (
            <button className="flex-1 py-2 text-[11.5px]" style={{ color: "var(--red)" }} disabled data-testid={`fn-${fn.name}-wrongnet`}>Switch to Blokista</button>
          ) : (
            <button className="btn-amber flex-1 py-2 text-[12px]" onClick={runWrite} disabled={busy} data-testid={`fn-${fn.name}-run`}>
              {busy ? "…" : "Write / Send Tx"}
            </button>
          )}
        </div>
      )}
      {est && (
        est.error
          ? <div className="mt-2 text-[11px]" style={{ color: "var(--muted-2)" }} data-testid={`fn-${fn.name}-est`}>{est.error}</div>
          : <div className="mt-2 text-[11.5px] mono" style={{ color: "var(--blue)" }} data-testid={`fn-${fn.name}-est`}>Est. cost: ~{est.feeBcc.toFixed(8)} {est.symbol} ({est.gas.toLocaleString()} gas × {est.gasPriceGwei} gwei)</div>
      )}
      {out && <div className="mt-2 mono text-[11.5px] break-all p-2 rounded" style={{ background: "rgba(52,211,153,0.08)", color: "var(--green)" }} data-testid={`fn-${fn.name}-out`}>{out}</div>}
      {err && <div className="mt-2 mono text-[11.5px] break-all p-2 rounded" style={{ background: "rgba(242,85,90,0.1)", color: "var(--red)" }} data-testid={`fn-${fn.name}-err`}>{err}</div>}
    </div>
  );
}

// Etherscan-style Read/Write panels. Reads run server-side; writes require a connected wallet.
export default function ContractConsole({ address, abi, mode = "both" }) {
  const list = abi || [];
  const readFns = list.filter((f) => f.type === "function" && ["view", "pure"].includes(f.stateMutability));
  const writeFns = list.filter((f) => f.type === "function" && !["view", "pure"].includes(f.stateMutability));
  const readPanel = (
    <Panel title="Read Contract" action={<span className="pill pill-muted">{readFns.length}</span>} testId="panel-read-contract">
      <div className="p-4 space-y-3">
        {readFns.map((f) => <FuncForm key={f.name} address={address} abi={abi} fn={f} mode="read" />)}
        {readFns.length === 0 && <div className="text-center py-4" style={{ color: "var(--muted)" }}>No read functions</div>}
      </div>
    </Panel>
  );
  const writePanel = (
    <Panel title="Write Contract" action={<span className="pill pill-amber">{writeFns.length}</span>} testId="panel-write-contract">
      <div className="p-4 space-y-3">
        {writeFns.map((f) => <FuncForm key={f.name} address={address} abi={abi} fn={f} mode="write" />)}
        {writeFns.length === 0 && <div className="text-center py-4" style={{ color: "var(--muted)" }}>No write functions</div>}
      </div>
    </Panel>
  );
  if (mode === "read") return <div data-testid="contract-console">{readPanel}</div>;
  if (mode === "write") return <div data-testid="contract-console">{writePanel}</div>;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="contract-console">
      {readPanel}
      {writePanel}
    </div>
  );
}
